/**
 * Block Renderer Component
 * Embeds existing element players for rendering
 */

import * as React from 'react';
import { cn } from '@ai-content-hub/content-kit';
import { imageMaxHeightVars } from '../image/imageStyle';
import type { BlockContent } from './types';
import {
    toTextElementData,
    toImageElementData,
    toAudioElementData,
    toVideoElementData,
    toRichTextElementData,
} from './types';

// Import existing element players
import { TextPlayer } from '../text/TextPlayer';
import { ImagePlayer } from '../image/ImagePlayer';
import { AudioPlayer } from '../audio/AudioPlayer';
import { VideoPlayer } from '../video/VideoPlayer';
import { RichTextPlayer } from '../rich-text/RichTextPlayer';
import { OfficeDocumentPlayer } from '../office-document/OfficeDocumentPlayer';

/** Text size options for text and rich-text blocks */
export type BlockRendererTextSize = 'sm' | 'base' | 'lg' | 'xl';

const TEXT_SIZE_CLASSES: Record<BlockRendererTextSize, string> = {
    sm: 'text-sm',
    base: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
};

export interface BlockRendererProps {
    /** Content to render */
    content: BlockContent;
    /** Additional class names */
    className?: string;
    /** Compact mode for inline rendering */
    compact?: boolean;
    /** Text size for text and rich-text blocks */
    textSize?: BlockRendererTextSize;
    /** Max width for media blocks (image, audio, video) - CSS value e.g. "200px", "50%" */
    mediaMaxWidth?: string | number;
    /** Max height for media blocks (image, audio, video) - CSS value e.g. "200px", "50%" */
    mediaMaxHeight?: string | number;
}

/**
 * Block Renderer that embeds existing element players
 * Uses the same data structure as the original elements
 */
function toCssValue(value: string | number): string {
    return typeof value === 'number' ? `${value}px` : value;
}

export const BlockRenderer: React.FC<BlockRendererProps> = ({
    content,
    className,
    compact = true,
    textSize,
    mediaMaxWidth,
    mediaMaxHeight,
}) => {
    const wrapperClass = cn(
        'block-renderer',
        compact && 'block-renderer--compact',
        className
    );

    const textSizeClass = textSize ? TEXT_SIZE_CLASSES[textSize] : undefined;

    const hasMediaMaxWidth = mediaMaxWidth != null;
    const hasMediaMaxHeight = mediaMaxHeight != null;
    const hasMediaMax = hasMediaMaxWidth || hasMediaMaxHeight;

    const mediaStyle = hasMediaMax
        ? {
              // EAA-2063: the image's own viewport cap yields to this box's height limit.
              ...(hasMediaMaxHeight ? imageMaxHeightVars('100%') : {}),
              maxWidth: hasMediaMaxWidth ? toCssValue(mediaMaxWidth!) : undefined,
              maxHeight: hasMediaMaxHeight ? toCssValue(mediaMaxHeight!) : undefined,
              // When both are set, use explicit dimensions so object-contain can fit content
              ...(hasMediaMaxWidth &&
                  hasMediaMaxHeight && {
                      width: toCssValue(mediaMaxWidth!),
                      height: toCssValue(mediaMaxHeight!),
                  }),
          }
        : undefined;

    const mediaWrapperClass = mediaStyle
        ? cn(
              wrapperClass,
              'block-renderer-media overflow-hidden flex items-center justify-center min-w-0 min-h-0',
              '[&_.image-element]:max-w-full [&_.image-element]:max-h-full [&_.image-element]:w-auto [&_.image-element]:m-auto',
              '[&_.image-element_img]:object-contain [&_.image-element_img]:max-w-full [&_.image-element_img]:max-h-full',
              '[&_.video-element]:max-w-full [&_.video-element]:max-h-full',
              '[&_.video-element>div]:max-w-full [&_.video-element>div]:max-h-full',
              '[&_.audio-element]:max-w-full [&_.audio-element]:max-h-full'
          )
        : wrapperClass;

    // Use a plain wrapper function instead of an inline component to avoid
    // unmounting/remounting children on every re-render (new component identity).
    const mediaWrapperProps = mediaStyle
        ? { className: mediaWrapperClass, style: mediaStyle }
        : { className: wrapperClass };

    // Runtime safety: if content is a raw string (not a BlockContent object),
    // render it directly — detect HTML and handle accordingly.
    if (typeof content === 'string' || !content?.type) {
        const raw = typeof content === 'string' ? content : '';
        if (!raw) return null;
        const hasHtml = /<[a-zA-Z][^>]*>/.test(raw);
        return (
            <div className={cn(wrapperClass, 'min-w-0')}>
                {hasHtml ? (
                    <div
                        className={cn('rich-text-element break-words', textSizeClass)}
                        dangerouslySetInnerHTML={{ __html: raw }}
                    />
                ) : (
                    <p className={cn('break-words', textSizeClass)}>{raw}</p>
                )}
            </div>
        );
    }

    switch (content.type) {
        case 'text': {
            // Auto-detect HTML in text content (from Moodle import) and render as rich-text
            const textData = toTextElementData(content);
            const textStr = textData.text || '';
            const hasHtmlTags = /<[a-zA-Z][^>]*>/.test(textStr);

            if (hasHtmlTags) {
                return (
                    <div className={cn(wrapperClass, 'min-w-0')}>
                        <div
                            className={cn(
                                'rich-text-element break-words [&>*]:break-words',
                                // Flatten paragraph margins for inline-like rendering in MCQ choices
                                '[&>p]:m-0 [&>p:last-child]:mb-0',
                                textSizeClass
                            )}
                            dangerouslySetInnerHTML={{ __html: textStr }}
                        />
                    </div>
                );
            }

            return (
                <div className={cn(wrapperClass, 'min-w-0')}>
                    <TextPlayer
                        data={textData}
                        className={cn('break-words', textSizeClass)}
                    />
                </div>
            );
        }

        case 'image': {
            const imageData = toImageElementData(content);
            return (
                <div {...mediaWrapperProps}>
                    <ImagePlayer data={{ ...imageData, size: 'full' }} />
                </div>
            );
        }

        case 'audio':
            return (
                <div {...mediaWrapperProps}>
                    <AudioPlayer data={toAudioElementData(content)} />
                </div>
            );

        case 'video':
            return (
                <div {...mediaWrapperProps}>
                    <VideoPlayer data={toVideoElementData(content)} />
                </div>
            );

        case 'rich-text':
            return (
                <RichTextPlayer
                    data={toRichTextElementData(content)}
                    className={cn(wrapperClass, 'break-words [&_*]:break-words min-w-0', textSizeClass)}
                />
            );

        case 'office-document':
            return (
                <div className={cn(wrapperClass, 'min-w-0 w-full')}>
                    <OfficeDocumentPlayer data={{ src: content.src, title: content.title, fileName: content.fileName }} />
                </div>
            );

        default:
            return (
                <span className="text-muted-foreground italic">
                    Unknown content type
                </span>
            );
    }
};

export default BlockRenderer;
